import * as THREE from 'three'

import { COCKPIT, ENV } from '../config/constants.js'
import { DEG, damp } from '../util/units.js'
import { headSwayM, setHead, stepHead } from './headLag.js'
import {
  applyDrag,
  applyZoom,
  makeAllViewStates,
  orbitOffset,
  recentre,
} from './viewControl.js'

/**
 * Camera views.
 *
 * Six views, and the choice of which six is not arbitrary — each one exists to
 * teach something specific about the recovery:
 *
 *   1 COCKPIT   the only view where the ball, the AoA indexer and the deck are
 *               in the same picture. This is the one you learn in.
 *   2 CHASE     what your wingman sees. Best for reading attitude and closure.
 *   3 ORBIT     the same camera with the aeroplane's heading taken out, so it
 *               stays put in the world and the jet flies past it.
 *   4 LSO       from the platform at the port deck edge, looking up the groove.
 *               This is where you find out what your approach actually looked
 *               like to the person waving you.
 *   5 PLAT      the PLAT camera, buried in the deck on the centreline looking
 *               aft. Shows lineup and hook-to-ramp better than anything else.
 *   6 TOWER     from the island, the whole pattern at once.
 *
 * ALL SIX are mouse-draggable and wheel-zoomable, and each remembers where you
 * left it. See viewControl.js: the two orbital views move the camera, the four
 * fixed ones turn it and zoom a lens, and that is the only difference.
 */

export const VIEWS = {
  COCKPIT: 1,
  CHASE: 2,
  ORBIT: 3,
  LSO: 4,
  PLAT: 5,
  TOWER: 6,
}

/** Maps a view number to its key in viewControl.js. */
export const VIEW_KEYS = {
  1: 'cockpit',
  2: 'chase',
  3: 'orbit',
  4: 'lso',
  5: 'plat',
  6: 'tower',
}

export const VIEW_NAMES = {
  1: 'COCKPIT',
  2: 'CHASE',
  3: 'ORBIT',
  4: 'LSO PLATFORM',
  5: 'PLAT',
  6: 'TOWER',
}

export class CameraRig {
  constructor(aspect) {
    this.camera = new THREE.PerspectiveCamera(58, aspect, 0.28, ENV.VISIBILITY_M * 3)
    this.view = VIEWS.COCKPIT

    /**
     * Look state for EVERY view, kept separately.
     *
     * Separately, because a view you have aimed somewhere should still be
     * aimed there when you come back to it. Sharing one yaw and pitch across
     * all six means looking over your shoulder in the cockpit silently swings
     * the tower camera off the ship.
     */
    this.views = makeAllViewStates()

    this._pos = new THREE.Vector3()
    this._target = new THREE.Vector3()
    this._up = new THREE.Vector3(0, 1, 0)
    this._smoothPos = new THREE.Vector3()
    this._smoothTarget = new THREE.Vector3()
    this._seat = new THREE.Vector3()
    this._sway = new THREE.Vector3()
    this._orbit = new THREE.Vector3()
    this._euler = new THREE.Euler(0, 0, 0, 'YXZ')
    /** The pilot's head, which lags the airframe slightly. See headLag.js. */
    this._head = { headingDeg: 0, pitchDeg: 0, rollDeg: 0 }

    /** Live seat height, seeded from the configured offset. */
    this.seatOffset = [...(COCKPIT.seatOffsetM ?? [0, 0, 0])]
    this._initialised = false
  }

  setView(view) {
    if (this.view === view) return
    this.view = view
    this._initialised = false
  }

  /** Look state for the view currently selected. */
  get look() {
    return this.views[VIEW_KEYS[this.view]]
  }

  setAspect(aspect) {
    this.camera.aspect = aspect
    this.camera.updateProjectionMatrix()
  }

  get isCockpit() {
    return this.view === VIEWS.COCKPIT
  }

  /**
   * @param {object} ctx
   * @param {import('./F14Model.js').F14Model} ctx.aircraft
   * @param {import('../rails/RailsFlight.js').RailState} ctx.state
   * @param {import('../world/Carrier.js').Carrier} ctx.carrier
   * @param {number} ctx.dt
   */
  update({ aircraft, state, carrier, dt }) {
    switch (this.view) {
      case VIEWS.COCKPIT:
        this._cockpit(aircraft, state, dt)
        break
      case VIEWS.CHASE:
        this._chase(aircraft, state, dt)
        break
      case VIEWS.ORBIT:
        this._orbitView(aircraft, dt)
        break
      case VIEWS.LSO:
        this._lso(aircraft, carrier, dt)
        break
      case VIEWS.PLAT:
        this._plat(aircraft, carrier, dt)
        break
      case VIEWS.TOWER:
        this._tower(aircraft, carrier, dt)
        break
      default:
        this._cockpit(aircraft, state, dt)
    }
    this._initialised = true
  }

  /* ------------------------------------------------------------------ */

  _cockpit(aircraft, state, dt) {
    aircraft.getEyeWorld(this._pos)

    // Seat adjustment, applied in the aircraft's own frame so that "up" means
    // up relative to the pilot rather than to the world. It moves the VIEW
    // only — `aircraft.eye` stays exactly where it is, because that is the
    // point the ball is flown from and the grading is measured against.
    const [sx, sy, sz] = this.seatOffset
    if (sx || sy || sz) {
      this._pos.add(this._seat.set(sx, sy, sz).applyQuaternion(state.quaternion))
    }

    // A head is on a neck. Rolling the aeroplane swings the pilot's head a
    // couple of centimetres sideways before their body catches up, and that
    // tiny lateral shift is most of what distinguishes a person sitting in a
    // cockpit from a camera bolted to one.
    const sway = headSwayM(state.rollRateDegS ?? 0)
    if (sway) this._pos.add(this._sway.set(sway, 0, 0).applyQuaternion(state.quaternion))

    this.camera.position.copy(this._pos)

    // The camera takes the HEAD's attitude, not the airframe's — see
    // headLag.js. Copying the airframe quaternion straight across is one line
    // and is why simulator cockpit footage never looks like the real thing.
    if (!this._initialised) setHead(this._head, state)
    else stepHead(this._head, state, dt)

    this._euler.set(
      this._head.pitchDeg * DEG,
      -this._head.headingDeg * DEG,
      -this._head.rollDeg * DEG,
      'YXZ',
    )
    this.camera.quaternion.setFromEuler(this._euler)
    // Look-around is applied in the head's own frame, so looking left while
    // inverted does what your neck would do.
    const look = this.look
    this.camera.rotateY(look.yaw)
    this.camera.rotateX(look.pitch)
    this.camera.fov = look.fov
    this.camera.near = look.near
    this.camera.updateProjectionMatrix()
  }

  _chase(aircraft, state, dt) {
    // An orbit around the jet in its HEADING frame, not a fixed offset in its
    // full attitude frame.
    //
    // The old version took the whole quaternion and then patched the vertical
    // component back down with a max() so the camera did not get flung when the
    // aeroplane pitched — a fix for a symptom of using the wrong frame. Heading
    // only means the camera stays astern through the break without ever rolling
    // or pitching with the jet, so the horizon stays a usable reference, and it
    // means the user's drag is simply another rotation about the same axis.
    this._orbitAround(aircraft.group.position, state.headingDeg * DEG, dt, 6.5)
    this.camera.updateProjectionMatrix()
  }

  _orbitView(aircraft, dt) {
    // The free orbit is the same manoeuvre with no heading in it — the camera
    // stays where you left it in WORLD terms while the jet flies past.
    this._orbitAround(aircraft.group.position, 0, dt, 12)
    this.camera.updateProjectionMatrix()
  }

  /**
   * Place the camera on a sphere around a point and look back at it.
   *
   * @param {THREE.Vector3} centre what to look at
   * @param {number} frameRad heading frame to orbit in; 0 for world-fixed
   */
  _orbitAround(centre, frameRad, dt, rate) {
    const look = this.look
    const o = orbitOffset(frameRad, look.yaw, look.pitch, look.dist)
    this._pos.copy(centre).add(this._orbit.set(o.x, o.y, o.z))
    // Never underwater, and never below the deck when close aboard. Dragging
    // the camera under the sea is a way to lose the aeroplane entirely and
    // wonder whether the trainer has crashed.
    this._pos.y = Math.max(this._pos.y, 2)
    this._target.copy(centre)
    this._apply(dt, rate)
    this.camera.fov = look.fov
    this.camera.near = look.near
  }

  _lso(aircraft, carrier, dt) {
    carrier.lsoWorld(this._pos)
    this._pos.y += 1.6
    aircraft.getEyeWorld(this._target)
    // Narrow field by default: the LSO is reading a jet a thousand feet away.
    this._aimed(dt, 9)
  }

  _plat(aircraft, carrier, dt) {
    // Buried on the angled deck centreline just forward of the wires, looking
    // back down the groove.
    carrier.deckToWorld(120, 0, 1.5, this._pos)
    aircraft.getEyeWorld(this._target)
    this._aimed(dt, 10)
  }

  _tower(aircraft, carrier, dt) {
    carrier.shipToWorld(60, 88, 62, this._pos)
    aircraft.group.getWorldPosition(this._target)
    this._aimed(dt, 7)
  }

  /**
   * A camera that is somewhere fixed and tracks the aeroplane, with the user
   * free to turn it off that track.
   *
   * The offset is applied AFTER the automatic aim, which is the difference
   * between a control that helps and one that fights you: drag twenty degrees
   * left of the jet and you stay twenty degrees left of it as it moves, rather
   * than having the tracker drag the view back every frame.
   */
  _aimed(dt, rate) {
    const look = this.look
    this._apply(dt, rate)
    if (look.yaw) this.camera.rotateY(look.yaw)
    if (look.pitch) this.camera.rotateX(look.pitch)
    this.camera.fov = look.fov
    this.camera.near = look.near
    this.camera.updateProjectionMatrix()
  }

  /** Smooth toward the computed position and aim point. */
  _apply(dt, rate) {
    if (!this._initialised) {
      this._smoothPos.copy(this._pos)
      this._smoothTarget.copy(this._target)
    } else {
      this._smoothPos.set(
        damp(this._smoothPos.x, this._pos.x, rate, dt),
        damp(this._smoothPos.y, this._pos.y, rate, dt),
        damp(this._smoothPos.z, this._pos.z, rate, dt),
      )
      this._smoothTarget.set(
        damp(this._smoothTarget.x, this._target.x, rate * 1.6, dt),
        damp(this._smoothTarget.y, this._target.y, rate * 1.6, dt),
        damp(this._smoothTarget.z, this._target.z, rate * 1.6, dt),
      )
    }
    this.camera.position.copy(this._smoothPos)
    this.camera.up.set(0, 1, 0)
    this.camera.lookAt(this._smoothTarget)
  }

  /**
   * Mouse look. Works in ALL SIX views.
   *
   * It used to work in two of them, and in the other four the handler ran, did
   * nothing, and said nothing about why — so the chase view read as broken
   * rather than as fixed, which is exactly what a third-person camera you
   * cannot move looks like.
   */
  applyMouseDelta(dx, dy) {
    applyDrag(this.look, dx, dy)
  }

  /** Wheel: range in the orbital views, field of view in the fixed ones. */
  applyZoom(delta) {
    applyZoom(this.look, delta)
  }

  /** Num 5. Recentres the view you are IN, and leaves the others alone. */
  recentreLook() {
    recentre(this.look)
  }

  /**
   * Raise or lower the seat, and say what the number is.
   *
   * The console line is the point. Guessing a cockpit's eye height from its
   * bounding box is a losing game — every model puts its origin somewhere
   * different — so instead of asking anyone to edit a constant, reload, squint
   * and repeat, this prints the value to paste into COCKPIT.seatOffsetM once
   * it looks right.
   */
  adjustSeat(deltaM) {
    this.seatOffset[1] = THREE.MathUtils.clamp(this.seatOffset[1] + deltaM, -1.6, 1.2)
    const rounded = this.seatOffset.map((v) => Math.round(v * 100) / 100)
    console.info(
      `[cockpit] seat ${rounded[1] >= 0 ? '+' : ''}${rounded[1].toFixed(2)} m — ` +
        `to keep it, set COCKPIT.seatOffsetM to [${rounded.join(', ')}] in src/config/constants.js`,
    )
    return rounded[1]
  }
}
